import {
  string as yupString,
  object as yupObject,
} from 'yup';
import merge from 'lodash/merge';

import type { ScenarioDetailRow } from '@queries/scenarios';
import {
  RowMessages,
  ValidateRowParams,
  ValidateRowsFn,
} from '../../../shared/gridEdit/useEditableGridLogic';
import { validateBaseSchema } from '../../../shared/gridEdit/validators';
import { ReportDetailsGridContext } from './types';

type Validator = (...params: ValidateRowParams<ScenarioDetailRow, ReportDetailsGridContext>) =>
  RowMessages<ScenarioDetailRow>;

const ERROR_MESSAGES = {
  required: (fieldName: string) => `${fieldName} is required`,
};

export const detailsRowSchema = yupObject().shape({
  account: yupString().required(ERROR_MESSAGES.required('Account is required')),
  detailName: yupString().required(ERROR_MESSAGES.required('Description is required')),
});

export const checkEntityForDuplicate: Validator = (
  detailRow,
  _,
  allDetailRows
) => {
  if (!detailRow.meta.isAdded) {
    return {};
  }

  const duplicates = allDetailRows.filter(entity => (
    entity.account === detailRow.account
    && entity.detailName === detailRow.detailName
    && !entity.meta.isAdded
  ));

  if (duplicates.length) {
    const messages = [
      'Detail row for selected account and description already exists',
    ];
    return {
      account: messages,
      detailName: messages,
    };
  }

  return {};
};

export const validateEditableFields: Validator = (detailRow, { editableDetailKeys }) => {
  if (!Object.keys(detailRow.details).length) {
    return editableDetailKeys.reduce((errors, detailKey) => {
      errors[`details.${detailKey}.value`] = ['At least one detail value has to be entered'];
      return errors;
    }, {} as Record<string, string[]>);
  }

  return {};
};

export const validateDetailRow: ValidateRowsFn<ScenarioDetailRow, ReportDetailsGridContext> = (
  detailRow,
  context,
  allDetailRows
) => {
  const baseSchemaErrors = validateBaseSchema(detailsRowSchema, detailRow, allDetailRows);
  const duplicateErrors = checkEntityForDuplicate(detailRow, context, allDetailRows);
  const detailValuesErrors = validateEditableFields(detailRow, context, allDetailRows);

  return {
    errors: merge({}, baseSchemaErrors, duplicateErrors, detailValuesErrors),
  };
};
